<template>
  <div class="gitea-page">
    <div class="gitea-panel">
      <div class="gitea-toolbar">
        <div class="gitea-toolbar-text">
          <div class="gitea-toolbar-title-row">
            <span class="gitea-title">Gitea 代码仓库</span>
            <span
              class="gitea-health-dot"
              :class="health.ok ? 'is-ok' : health.checked ? 'is-bad' : 'is-idle'"
            />
            <span class="gitea-health-label">{{ healthLabel }}</span>
            <span v-if="health.version" class="gitea-version">v{{ health.version }}</span>
          </div>
          <span class="gitea-toolbar-desc">管理平台 Gitea 用户与仓库，操作经服务端代理完成</span>
        </div>
        <div class="gitea-toolbar-actions">
          <Button size="small" icon="md-heart" :loading="healthLoading" @click="checkHealthy">连通性</Button>
          <Button size="small" icon="md-refresh" @click="refreshCurrent">刷新</Button>
        </div>
      </div>

      <Tabs v-model="activeTab" :animated="false" class="gitea-tabs" @on-click="onTabChange">
        <TabPane label="用户" name="users">
          <div class="gitea-panel-toolbar">
            <Input
              v-model="userFilter"
              search
              clearable
              placeholder="按用户名 / 邮箱 / 显示名筛选"
              class="gitea-search"
            />
            <div class="gitea-toolbar-actions">
              <Button type="primary" icon="md-add" @click="openCreateUser">新建用户</Button>
            </div>
          </div>
          <div class="gitea-table-wrap">
            <BaseTable
              full
              border
              body-scroll-x="hidden"
              :option="{ stripe: true }"
              :columns="userColumns"
              :data="filteredUsers"
              :loading="userLoading"
            />
          </div>
        </TabPane>

        <TabPane label="仓库" name="repos">
          <div class="gitea-panel-toolbar gitea-panel-toolbar--repos">
            <Input
              v-model="repoQuery"
              search
              clearable
              placeholder="搜索仓库关键字"
              class="gitea-search gitea-search--sm"
              @on-search="fetchRepos"
              @on-enter="fetchRepos"
            />
            <Input
              v-model="repoUser"
              clearable
              placeholder="按用户列出"
              class="gitea-filter-input"
              @on-enter="fetchRepos"
            />
            <Input
              v-model="repoOrg"
              clearable
              placeholder="按组织列出"
              class="gitea-filter-input"
              @on-enter="fetchRepos"
            />
            <Button @click="fetchRepos">查询</Button>
            <div class="gitea-toolbar-actions">
              <Button type="primary" icon="md-add" @click="openCreateRepo">新建仓库</Button>
            </div>
          </div>
          <div class="gitea-table-wrap">
            <BaseTable
              full
              border
              body-scroll-x="hidden"
              :option="{ stripe: true }"
              :columns="repoColumns"
              :data="repos"
              :loading="repoLoading"
            />
          </div>
        </TabPane>
      </Tabs>
    </div>

    <!-- 新建 / 编辑用户 -->
    <Modal
      v-model="userModalVisible"
      :title="userModalMode === 'create' ? '新建用户' : '编辑用户'"
      :loading="userSubmitting"
      width="560"
      @on-ok="handleUserSubmit"
    >
      <Form ref="userFormRef" :model="userForm" :rules="userRules" :label-width="110">
        <FormItem v-if="userModalMode === 'create'" label="用户名" prop="username">
          <Input v-model="userForm.username" placeholder="登录用户名" />
        </FormItem>
        <FormItem v-else label="用户名">
          <Input :value="userForm.username" disabled />
        </FormItem>
        <FormItem label="邮箱" prop="email">
          <Input v-model="userForm.email" placeholder="邮箱" />
        </FormItem>
        <FormItem v-if="userModalMode === 'create'" label="初始密码" prop="password">
          <Input v-model="userForm.password" type="password" password placeholder="初始密码" />
        </FormItem>
        <FormItem label="显示名">
          <Input v-model="userForm.full_name" placeholder="显示名" />
        </FormItem>
        <FormItem label="可见性">
          <Select v-model="userForm.visibility" style="width: 100%">
            <Option value="public">public</Option>
            <Option value="limited">limited</Option>
            <Option value="private">private</Option>
          </Select>
        </FormItem>
        <FormItem v-if="userModalMode === 'create'" label="强制改密">
          <i-switch v-model="userForm.must_change_password" />
        </FormItem>
        <FormItem v-if="userModalMode === 'create'" label="发送通知">
          <i-switch v-model="userForm.send_notify" />
        </FormItem>
        <template v-if="userModalMode === 'edit'">
          <FormItem label="激活">
            <i-switch v-model="userForm.active" />
          </FormItem>
          <FormItem label="禁止登录">
            <i-switch v-model="userForm.prohibit_login" />
          </FormItem>
          <FormItem label="简介">
            <Input v-model="userForm.description" type="textarea" :rows="2" />
          </FormItem>
        </template>
      </Form>
    </Modal>

    <!-- 重置密码 -->
    <Modal
      v-model="pwdModalVisible"
      title="重置密码"
      :loading="pwdSubmitting"
      @on-ok="handleResetPassword"
    >
      <Form ref="pwdFormRef" :model="pwdForm" :rules="pwdRules" :label-width="110">
        <FormItem label="用户名">
          <Input :value="pwdForm.username" disabled />
        </FormItem>
        <FormItem label="新密码" prop="new_password">
          <Input v-model="pwdForm.new_password" type="password" password placeholder="新密码" />
        </FormItem>
        <FormItem label="强制改密">
          <i-switch v-model="pwdForm.must_change_password" />
        </FormItem>
      </Form>
    </Modal>

    <!-- 新建 / 编辑仓库 -->
    <Modal
      v-model="repoModalVisible"
      :title="repoModalMode === 'create' ? '新建仓库' : '编辑仓库'"
      :loading="repoSubmitting"
      width="600"
      @on-ok="handleRepoSubmit"
    >
      <Form ref="repoFormRef" :model="repoForm" :rules="repoRules" :label-width="110">
        <template v-if="repoModalMode === 'create'">
          <FormItem label="仓库名" prop="name">
            <Input v-model="repoForm.name" placeholder="仓库名称" />
          </FormItem>
          <FormItem label="代用户创建">
            <Input v-model="repoForm.username" placeholder="可选，管理员代该用户创建" />
          </FormItem>
          <FormItem label="组织">
            <Input v-model="repoForm.org" placeholder="可选，在组织下创建" />
          </FormItem>
          <FormItem label="描述">
            <Input v-model="repoForm.description" type="textarea" :rows="2" />
          </FormItem>
          <FormItem label="私有">
            <i-switch v-model="repoForm.private" />
          </FormItem>
          <FormItem label="自动初始化">
            <i-switch v-model="repoForm.auto_init" />
          </FormItem>
          <FormItem label="默认分支">
            <Input v-model="repoForm.default_branch" placeholder="如 main" />
          </FormItem>
        </template>
        <template v-else>
          <FormItem label="完整名">
            <Input :value="repoForm.full_name" disabled />
          </FormItem>
          <FormItem label="仓库名">
            <Input v-model="repoForm.name" placeholder="新仓库名" />
          </FormItem>
          <FormItem label="描述">
            <Input v-model="repoForm.description" type="textarea" :rows="2" />
          </FormItem>
          <FormItem label="网站">
            <Input v-model="repoForm.website" />
          </FormItem>
          <FormItem label="默认分支">
            <Input v-model="repoForm.default_branch" />
          </FormItem>
          <FormItem label="私有">
            <i-switch v-model="repoForm.private" />
          </FormItem>
          <FormItem label="归档">
            <i-switch v-model="repoForm.archived" />
          </FormItem>
          <div class="gitea-switch-row">
            <FormItem label="Issues">
              <i-switch v-model="repoForm.has_issues" />
            </FormItem>
            <FormItem label="Wiki">
              <i-switch v-model="repoForm.has_wiki" />
            </FormItem>
            <FormItem label="Pull Requests">
              <i-switch v-model="repoForm.has_pull_requests" />
            </FormItem>
          </div>
        </template>
      </Form>
    </Modal>
  </div>
</template>

<script>
const emptyUserForm = () => ({
  username: '',
  email: '',
  password: '',
  full_name: '',
  visibility: 'public',
  must_change_password: false,
  send_notify: false,
  active: true,
  prohibit_login: false,
  description: '',
});

const emptyRepoForm = () => ({
  name: '',
  username: '',
  org: '',
  description: '',
  private: true,
  auto_init: true,
  default_branch: 'main',
  full_name: '',
  owner: '',
  original_name: '',
  website: '',
  archived: false,
  has_issues: true,
  has_wiki: true,
  has_pull_requests: true,
});

export default {
  data() {
    return {
      activeTab: 'users',
      health: { ok: false, version: '', checked: false },
      healthLoading: false,

      users: [],
      userFilter: '',
      userLoading: false,
      userModalVisible: false,
      userModalMode: 'create',
      userSubmitting: false,
      userForm: emptyUserForm(),

      pwdModalVisible: false,
      pwdSubmitting: false,
      pwdForm: { username: '', new_password: '', must_change_password: false },
      pwdRules: {
        new_password: [{ required: true, message: '请输入新密码', trigger: 'blur' }],
      },

      repos: [],
      repoQuery: '',
      repoUser: '',
      repoOrg: '',
      repoLoading: false,
      repoModalVisible: false,
      repoModalMode: 'create',
      repoSubmitting: false,
      repoForm: emptyRepoForm(),
      repoRules: {
        name: [{ required: true, message: '请输入仓库名', trigger: 'blur' }],
      },
    };
  },
  computed: {
    healthLabel() {
      if (!this.health.checked) return '未检测';
      return this.health.ok ? '已连通' : '不可用';
    },
    userRules() {
      const rules = {
        email: [
          { required: true, message: '请输入邮箱', trigger: 'blur' },
          { type: 'email', message: '邮箱格式不正确', trigger: 'blur' },
        ],
      };
      if (this.userModalMode === 'create') {
        rules.username = [{ required: true, message: '请输入用户名', trigger: 'blur' }];
        rules.password = [{ required: true, message: '请输入初始密码', trigger: 'blur' }];
      }
      return rules;
    },
    filteredUsers() {
      const kw = (this.userFilter || '').trim().toLowerCase();
      if (!kw) return this.users;
      return this.users.filter((u) => {
        const fields = [u.login, u.full_name, u.email, u.login_name];
        return fields.some((f) => f && String(f).toLowerCase().includes(kw));
      });
    },
    userColumns() {
      return [
        { type: 'index', width: 56, title: '#', align: 'left', className: 'gitea-col-index' },
        {
          title: '用户',
          key: 'login',
          minWidth: 220,
          align: 'left',
          className: 'gitea-col-main',
          render: (h, { row }) => this.renderUserCell(h, row),
        },
        {
          title: '邮箱',
          key: 'email',
          minWidth: 190,
          align: 'left',
          render: (h, { row }) => h('span', { class: 'gitea-cell-text' }, row.email || '—'),
        },
        {
          title: '角色',
          key: 'is_admin',
          width: 96,
          align: 'left',
          render: (h, { row }) => this.renderPill(h, row.is_admin ? '管理员' : '普通', row.is_admin ? 'blue' : 'muted'),
        },
        {
          title: '状态',
          key: 'active',
          width: 104,
          align: 'left',
          render: (h, { row }) => {
            if (row.prohibit_login) return this.renderPill(h, '禁止登录', 'warn');
            return this.renderPill(h, row.active ? '激活' : '禁用', row.active ? 'ok' : 'bad');
          },
        },
        {
          title: '最近登录',
          key: 'last_login',
          width: 156,
          align: 'left',
          render: (h, { row }) => h('span', { class: 'gitea-cell-muted' }, this.formatTime(row.last_login)),
        },
        {
          title: '创建时间',
          key: 'created',
          width: 156,
          align: 'left',
          render: (h, { row }) => h('span', { class: 'gitea-cell-muted' }, this.formatTime(row.created)),
        },
        {
          title: '操作',
          key: 'action',
          width: 196,
          align: 'left',
          className: 'gitea-col-action',
          render: (h, { row }) => h('div', { class: 'gitea-action-cell' }, [
            h('Button', {
              props: { size: 'small', type: 'text' },
              on: { click: () => this.openEditUser(row) },
            }, '编辑'),
            h('Button', {
              props: { size: 'small', type: 'text' },
              on: { click: () => this.openResetPassword(row) },
            }, '重置密码'),
            h('Button', {
              class: 'gitea-action-danger',
              props: { size: 'small', type: 'text' },
              on: { click: () => this.confirmDeleteUser(row) },
            }, '删除'),
          ]),
        },
      ];
    },
    repoColumns() {
      return [
        { type: 'index', width: 56, title: '#', align: 'left', className: 'gitea-col-index' },
        {
          title: '仓库',
          key: 'full_name',
          minWidth: 240,
          align: 'left',
          className: 'gitea-col-main',
          render: (h, { row }) => h('div', { class: 'gitea-repo-cell' }, [
            h('div', { class: 'gitea-repo-name' }, [
              h('a', {
                attrs: {
                  href: row.html_url || undefined,
                  target: row.html_url ? '_blank' : undefined,
                  rel: 'noopener noreferrer',
                },
              }, row.full_name || row.name || '—'),
            ]),
            h('div', { class: 'gitea-repo-desc' }, row.description || '暂无描述'),
          ]),
        },
        {
          title: '所有者',
          key: 'owner',
          width: 140,
          align: 'left',
          render: (h, { row }) => {
            const owner = row.owner || {};
            const login = owner.login || '—';
            const initial = login && login !== '—' ? String(login).charAt(0).toUpperCase() : '?';
            return h('div', { class: 'gitea-owner-cell' }, [
              owner.avatar_url
                ? h('img', {
                  class: 'gitea-avatar gitea-avatar--sm',
                  attrs: { src: owner.avatar_url, alt: login },
                  style: {
                    width: '22px',
                    height: '22px',
                    minWidth: '22px',
                    minHeight: '22px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    display: 'block',
                  },
                })
                : h('span', {
                  class: 'gitea-avatar gitea-avatar--sm gitea-avatar-fallback',
                  style: {
                    width: '22px',
                    height: '22px',
                    minWidth: '22px',
                    minHeight: '22px',
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                  },
                }, initial),
              h('span', { class: 'gitea-cell-text' }, login),
            ]);
          },
        },
        {
          title: '可见性',
          key: 'private',
          width: 88,
          align: 'left',
          render: (h, { row }) => this.renderPill(h, row.private ? '私有' : '公开', row.private ? 'warn' : 'ok'),
        },
        {
          title: '语言',
          key: 'language',
          width: 100,
          align: 'left',
          render: (h, { row }) => h('span', { class: 'gitea-cell-muted' }, row.language || '—'),
        },
        {
          title: '默认分支',
          key: 'default_branch',
          width: 108,
          align: 'left',
          render: (h, { row }) => h('span', { class: 'gitea-branch' }, row.default_branch || '—'),
        },
        {
          title: 'Stars',
          key: 'stars_count',
          width: 72,
          align: 'left',
          render: (h, { row }) => h('span', { class: 'gitea-cell-muted' }, row.stars_count != null ? row.stars_count : 0),
        },
        {
          title: '更新时间',
          key: 'updated_at',
          width: 156,
          align: 'left',
          render: (h, { row }) => h('span', { class: 'gitea-cell-muted' }, this.formatTime(row.updated_at)),
        },
        {
          title: '操作',
          key: 'action',
          width: 168,
          align: 'left',
          className: 'gitea-col-action',
          render: (h, { row }) => h('div', { class: 'gitea-action-cell' }, [
            h('Button', {
              props: { size: 'small', type: 'text' },
              on: { click: () => this.openEditRepo(row) },
            }, '编辑'),
            h('Button', {
              props: { size: 'small', type: 'text' },
              on: { click: () => this.copyText(row.clone_url || '') },
            }, '克隆'),
            h('Button', {
              class: 'gitea-action-danger',
              props: { size: 'small', type: 'text' },
              on: { click: () => this.confirmDeleteRepo(row) },
            }, '删除'),
          ]),
        },
      ];
    },
  },
  mounted() {
    this.$api.afterLogin(() => {
      this.checkHealthy();
      this.fetchUsers();
    });
  },
  methods: {
    unwrap(res) {
      const body = res && res.data;
      if (body && Object.prototype.hasOwnProperty.call(body, 'data')) {
        return body.data;
      }
      return body;
    },
    avatarInitial(row) {
      const name = (row && (row.login || row.full_name)) || '?';
      return String(name).charAt(0).toUpperCase();
    },
    renderUserCell(h, row) {
      const initial = this.avatarInitial(row);
      const avatarStyle = {
        width: '32px',
        height: '32px',
        minWidth: '32px',
        minHeight: '32px',
        maxWidth: '32px',
        maxHeight: '32px',
        borderRadius: '50%',
        objectFit: 'cover',
        flexShrink: 0,
        display: 'block',
        background: 'var(--border)',
      };
      return h('div', { class: 'gitea-user-cell' }, [
        row.avatar_url
          ? h('img', {
            class: 'gitea-avatar',
            attrs: { src: row.avatar_url, alt: row.login || '' },
            style: avatarStyle,
          })
          : h('span', {
            class: 'gitea-avatar gitea-avatar-fallback',
            style: {
              ...avatarStyle,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--subtext2)',
            },
          }, initial),
        h('div', { class: 'gitea-user-meta' }, [
          h('div', { class: 'gitea-user-login' }, row.login || '—'),
          h('div', { class: 'gitea-user-sub' }, row.full_name || '未设置显示名'),
        ]),
      ]);
    },
    renderPill(h, text, tone) {
      return h('span', { class: ['gitea-pill', `gitea-pill--${tone}`] }, text);
    },
    formatTime(v) {
      if (v == null || v === '' || v === '0001-01-01T00:00:00Z') return '—';
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return String(v);
      const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    repoOwnerName(row) {
      let owner = (row && row.owner && row.owner.login) || '';
      let name = (row && row.name) || '';
      if ((!owner || !name) && row && row.full_name && row.full_name.includes('/')) {
        const parts = row.full_name.split('/');
        owner = parts[0];
        name = parts.slice(1).join('/');
      }
      return { owner, name };
    },
    repoPath(owner, name) {
      return `/gitea/repos/${encodeURIComponent(owner)}/repo/${encodeURIComponent(name)}`;
    },
    async copyText(text) {
      if (!text) {
        this.$Message.warning('暂无克隆地址');
        return;
      }
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const el = document.createElement('textarea');
          el.value = text;
          document.body.appendChild(el);
          el.select();
          document.execCommand('copy');
          document.body.removeChild(el);
        }
        this.$Message.success('已复制');
      } catch (e) {
        this.$Message.error('复制失败');
      }
    },
    refreshCurrent() {
      if (this.activeTab === 'repos') this.fetchRepos();
      else this.fetchUsers();
    },
    onTabChange(name) {
      if (name === 'repos' && !this.repos.length && !this.repoLoading) {
        this.fetchRepos();
      }
    },
    async checkHealthy() {
      this.healthLoading = true;
      try {
        const data = this.unwrap(await this.$api.get('/gitea/healthy')) || {};
        this.health = {
          ok: !!data.ok,
          version: (data.version && data.version.version) || '',
          checked: true,
        };
      } catch (e) {
        this.health = { ok: false, version: '', checked: true };
        this.$Message.error((e && e.message) || '健康检查失败');
      } finally {
        this.healthLoading = false;
      }
    },
    async fetchUsers() {
      this.userLoading = true;
      try {
        const data = this.unwrap(await this.$api.get('/gitea/users', {
          params: { page: 1, limit: 50 },
        }));
        this.users = Array.isArray(data) ? data : [];
      } catch (e) {
        this.users = [];
        this.$Message.error((e && e.message) || '加载用户失败');
      } finally {
        this.userLoading = false;
      }
    },
    openCreateUser() {
      this.userModalMode = 'create';
      this.userForm = emptyUserForm();
      this.userModalVisible = true;
      this.$nextTick(() => {
        if (this.$refs.userFormRef) this.$refs.userFormRef.resetFields();
      });
    },
    openEditUser(row) {
      this.userModalMode = 'edit';
      this.userForm = {
        ...emptyUserForm(),
        username: row.login,
        email: row.email || '',
        full_name: row.full_name || '',
        visibility: row.visibility || 'public',
        active: row.active !== false,
        prohibit_login: !!row.prohibit_login,
        description: row.description || '',
      };
      this.userModalVisible = true;
    },
    async handleUserSubmit() {
      const valid = await this.$refs.userFormRef.validate();
      if (!valid) return false;
      this.userSubmitting = true;
      try {
        if (this.userModalMode === 'create') {
          const payload = {
            username: this.userForm.username.trim(),
            email: this.userForm.email.trim(),
            password: this.userForm.password,
            full_name: this.userForm.full_name,
            visibility: this.userForm.visibility,
            must_change_password: !!this.userForm.must_change_password,
            send_notify: !!this.userForm.send_notify,
          };
          await this.$api.post('/gitea/users', payload);
          this.$Message.success('用户已创建');
        } else {
          const username = this.userForm.username;
          const payload = {
            email: this.userForm.email.trim(),
            full_name: this.userForm.full_name,
            visibility: this.userForm.visibility,
            active: !!this.userForm.active,
            prohibit_login: !!this.userForm.prohibit_login,
            description: this.userForm.description,
          };
          await this.$api.patch(`/gitea/users/${encodeURIComponent(username)}`, payload);
          this.$Message.success('用户已更新');
        }
        this.userModalVisible = false;
        await this.fetchUsers();
        return true;
      } catch (e) {
        this.$Message.error((e && e.message) || '保存失败');
        return false;
      } finally {
        this.userSubmitting = false;
      }
    },
    openResetPassword(row) {
      this.pwdForm = {
        username: row.login,
        new_password: '',
        must_change_password: false,
      };
      this.pwdModalVisible = true;
      this.$nextTick(() => {
        if (this.$refs.pwdFormRef) this.$refs.pwdFormRef.resetFields();
      });
    },
    async handleResetPassword() {
      const valid = await this.$refs.pwdFormRef.validate();
      if (!valid) return false;
      this.pwdSubmitting = true;
      try {
        const username = this.pwdForm.username;
        await this.$api.post(`/gitea/users/${encodeURIComponent(username)}/password`, {
          new_password: this.pwdForm.new_password,
          must_change_password: !!this.pwdForm.must_change_password,
        });
        this.$Message.success('密码已重置');
        this.pwdModalVisible = false;
        return true;
      } catch (e) {
        this.$Message.error((e && e.message) || '重置失败');
        return false;
      } finally {
        this.pwdSubmitting = false;
      }
    },
    confirmDeleteUser(row) {
      this.$Modal.confirm({
        title: '确认删除用户',
        content: `确定删除 Gitea 用户「${row.login}」？此操作不可恢复。`,
        onOk: async () => {
          try {
            await this.$api.delete(`/gitea/users/${encodeURIComponent(row.login)}`);
            this.$Message.success('已删除');
            await this.fetchUsers();
          } catch (e) {
            this.$Message.error((e && e.message) || '删除失败');
          }
        },
      });
    },
    async fetchRepos() {
      this.repoLoading = true;
      try {
        const params = { page: 1, limit: 50 };
        if (this.repoUser.trim()) params.user = this.repoUser.trim();
        else if (this.repoOrg.trim()) params.org = this.repoOrg.trim();
        else if (this.repoQuery.trim()) params.q = this.repoQuery.trim();

        const data = this.unwrap(await this.$api.get('/gitea/repos', { params }));
        if (Array.isArray(data)) {
          this.repos = data;
        } else if (data && Array.isArray(data.data)) {
          this.repos = data.data;
        } else {
          this.repos = [];
        }
      } catch (e) {
        this.repos = [];
        this.$Message.error((e && e.message) || '加载仓库失败');
      } finally {
        this.repoLoading = false;
      }
    },
    openCreateRepo() {
      this.repoModalMode = 'create';
      this.repoForm = emptyRepoForm();
      this.repoModalVisible = true;
      this.$nextTick(() => {
        if (this.$refs.repoFormRef) this.$refs.repoFormRef.resetFields();
      });
    },
    openEditRepo(row) {
      const { owner, name } = this.repoOwnerName(row);
      this.repoModalMode = 'edit';
      this.repoForm = {
        ...emptyRepoForm(),
        name: name || '',
        original_name: name || '',
        full_name: row.full_name || '',
        owner: owner || '',
        description: row.description || '',
        website: row.website || '',
        private: !!row.private,
        default_branch: row.default_branch || 'main',
        archived: !!row.archived,
        has_issues: row.has_issues !== false,
        has_wiki: row.has_wiki !== false,
        has_pull_requests: row.has_pull_requests !== false,
      };
      this.repoModalVisible = true;
    },
    async handleRepoSubmit() {
      const valid = await this.$refs.repoFormRef.validate();
      if (!valid) return false;
      this.repoSubmitting = true;
      try {
        if (this.repoModalMode === 'create') {
          const payload = {
            name: this.repoForm.name.trim(),
            description: this.repoForm.description,
            private: !!this.repoForm.private,
            auto_init: !!this.repoForm.auto_init,
          };
          if (this.repoForm.username.trim()) payload.username = this.repoForm.username.trim();
          if (this.repoForm.org.trim()) payload.org = this.repoForm.org.trim();
          if (this.repoForm.default_branch.trim()) {
            payload.default_branch = this.repoForm.default_branch.trim();
          }
          await this.$api.post('/gitea/repos', payload);
          this.$Message.success('仓库已创建');
        } else {
          const owner = this.repoForm.owner;
          const originalName = this.repoForm.original_name;
          if (!owner || !originalName) {
            this.$Message.error('无法解析仓库路径');
            return false;
          }
          const payload = {
            name: this.repoForm.name.trim(),
            description: this.repoForm.description,
            website: this.repoForm.website,
            private: !!this.repoForm.private,
            default_branch: this.repoForm.default_branch,
            archived: !!this.repoForm.archived,
            has_issues: !!this.repoForm.has_issues,
            has_wiki: !!this.repoForm.has_wiki,
            has_pull_requests: !!this.repoForm.has_pull_requests,
          };
          await this.$api.patch(this.repoPath(owner, originalName), payload);
          this.$Message.success('仓库已更新');
        }
        this.repoModalVisible = false;
        await this.fetchRepos();
        return true;
      } catch (e) {
        this.$Message.error((e && e.message) || '保存失败');
        return false;
      } finally {
        this.repoSubmitting = false;
      }
    },
    confirmDeleteRepo(row) {
      const { owner, name } = this.repoOwnerName(row);
      this.$Modal.confirm({
        title: '确认删除仓库',
        content: `确定删除仓库「${row.full_name || row.name}」？此操作不可恢复。`,
        onOk: async () => {
          try {
            if (!owner || !name) throw new Error('无法解析仓库路径');
            await this.$api.delete(this.repoPath(owner, name));
            this.$Message.success('已删除');
            await this.fetchRepos();
          } catch (e) {
            this.$Message.error((e && e.message) || '删除失败');
          }
        },
      });
    },
  },
};
</script>

<style lang="less" scoped>
.gitea-page {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 14px 16px 16px;
  box-sizing: border-box;
  background: var(--bg3);
  color: var(--text2);
}

.gitea-panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--bg2);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.gitea-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  background: var(--bg2);
  flex-shrink: 0;
}

.gitea-toolbar-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.gitea-toolbar-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.gitea-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text2);
  letter-spacing: 0.02em;
}

.gitea-toolbar-desc {
  font-size: 12px;
  color: var(--subtext2);
}

.gitea-health-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--subtext2);
  flex-shrink: 0;

  &.is-ok { background: #19be6b; }
  &.is-bad { background: #ed4014; }
  &.is-idle { background: var(--subtext2); opacity: 0.55; }
}

.gitea-health-label,
.gitea-version {
  font-size: 12px;
  color: var(--subtext2);
}

.gitea-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
}

.gitea-tabs {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0 16px 16px;
  color: var(--text2);

  /deep/ .ivu-tabs-bar {
    margin-bottom: 10px;
    border-bottom-color: var(--border);
  }

  /deep/ .ivu-tabs-nav .ivu-tabs-tab {
    padding: 10px 16px;
    color: var(--subtext2);
  }

  /deep/ .ivu-tabs-nav .ivu-tabs-tab-active {
    color: var(--text2);
    font-weight: 600;
  }

  /deep/ .ivu-tabs-ink-bar {
    height: 2px;
  }

  /deep/ .ivu-tabs-content {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  /deep/ .ivu-tabs-tabpane {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
}

.gitea-panel-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 0 12px;
  flex-shrink: 0;
}

.gitea-panel-toolbar--repos {
  flex-wrap: wrap;
}

.gitea-search {
  width: 280px;
  max-width: 100%;
}

.gitea-search--sm {
  width: 200px;
}

.gitea-filter-input {
  width: 132px;
}

.gitea-table-wrap {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--bg2);

  /deep/ .gz-table,
  /deep/ .ivu-table-wrapper {
    flex: 1;
    min-height: 0;
    height: 100%;
  }

  /deep/ .gz-table {
    box-shadow: none !important;
  }

  /* 覆盖 BaseTable 全局 .ivu-table-cell { padding: 0 } */
  /deep/ .gz-table .ivu-table-cell,
  /deep/ .ivu-table .ivu-table-cell {
    padding: 10px 14px !important;
    width: auto;
    text-align: left;
  }

  /deep/ .ivu-table {
    font-size: 13px;
    color: var(--text2);
  }

  /deep/ .ivu-table th {
    font-weight: 600;
    color: var(--subtext2);
    background: rgba(0, 0, 0, 0.02);
  }

  /deep/ .ivu-table th .ivu-table-cell {
    padding: 11px 14px !important;
  }

  /deep/ .ivu-table td {
    height: auto;
  }

  /deep/ .ivu-table-tbody tr:hover td {
    background: rgba(45, 140, 240, 0.04);
  }

  /deep/ .ivu-table td,
  /deep/ .ivu-table th {
    border-color: var(--border);
  }

  /deep/ .gitea-col-main .ivu-table-cell {
    padding-top: 8px !important;
    padding-bottom: 8px !important;
  }
}

.gitea-switch-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;

  /deep/ .ivu-form-item {
    margin-bottom: 12px;
  }
}
</style>

<!-- 表格 render 节点不受 scoped 影响，单独提供全局样式 -->
<style lang="less">
.gitea-user-cell,
.gitea-owner-cell {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  text-align: left;
}

.gitea-owner-cell {
  align-items: center;
}

.gitea-avatar {
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  min-height: 32px !important;
  max-width: 32px !important;
  max-height: 32px !important;
  border-radius: 50% !important;
  object-fit: cover !important;
  flex-shrink: 0;
  background: var(--border);
  display: block;
  margin-top: 1px;
}

.gitea-avatar--sm {
  width: 22px !important;
  height: 22px !important;
  min-width: 22px !important;
  min-height: 22px !important;
  max-width: 22px !important;
  max-height: 22px !important;
  margin-top: 0;
}

.gitea-avatar-fallback {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--subtext2);
  background: rgba(0, 0, 0, 0.06);
}

.gitea-user-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
  text-align: left;
}

.gitea-user-login {
  font-size: 13px;
  font-weight: 600;
  color: var(--text2);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.gitea-user-sub {
  font-size: 12px;
  font-weight: 400;
  color: var(--subtext2);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.gitea-repo-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
  text-align: left;
}

.gitea-repo-name {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  a {
    color: var(--primary);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.gitea-repo-desc {
  font-size: 12px;
  color: var(--subtext2);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.gitea-cell-text {
  display: block;
  color: var(--text2);
  text-align: left;
  line-height: 1.4;
}

.gitea-cell-muted {
  display: block;
  color: var(--subtext2);
  font-size: 12px;
  text-align: left;
  line-height: 1.4;
}

.gitea-branch {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.04);
  color: var(--subtext2);
  font-size: 12px;
}

.gitea-pill {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
}

.gitea-pill--ok {
  color: #19be6b;
  background: rgba(25, 190, 107, 0.12);
}

.gitea-pill--bad {
  color: #ed4014;
  background: rgba(237, 64, 20, 0.12);
}

.gitea-pill--warn {
  color: #ff9900;
  background: rgba(255, 153, 0, 0.14);
}

.gitea-pill--blue {
  color: #2d8cf0;
  background: rgba(45, 140, 240, 0.12);
}

.gitea-pill--muted {
  color: var(--subtext2);
  background: rgba(0, 0, 0, 0.05);
}

.gitea-action-cell {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 2px;
  flex-wrap: nowrap;
}

.gitea-action-danger {
  color: #ed4014 !important;
}
</style>
